import { useMutation, useQueryClient } from '@tanstack/react-query'

import { dataOrThrow, type PostPetSubscriptionBody } from '@snootsvet/shared/portal'

import { usePortalApi } from '@/customer/hooks/use-api'
import { meQueryKey } from '@/customer/hooks/use-get-me'
import { petDiscountsQueryKey } from '@/customer/hooks/use-get-pet-discounts'
import { petSubscriptionQueryKey } from '@/customer/hooks/use-get-pet-subscription'
import { invoicesQueryKey } from '@/customer/hooks/use-list-invoices'

/**
 * Creates a Stripe subscription for the pet via
 * `POST /api/portal/pets/:id/subscription`.
 *
 * The backend uses `payment_behavior: 'allow_incomplete'`, so the
 * subscription is always created and the response is always 200.
 * The caller checks the subscription status to decide the next step;
 * when `false`, `incompletePayment` carries a `clientSecret` for
 * retrying via `stripe.confirmCardPayment`.
 */
export function useCreateSubscription(patientId: string) {
  const api = usePortalApi()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (body: PostPetSubscriptionBody) =>
      dataOrThrow(await api.post('/pets/:id/subscription', { params: { id: patientId }, body })),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: meQueryKey })
      queryClient.invalidateQueries({ queryKey: petSubscriptionQueryKey(patientId) })
      queryClient.invalidateQueries({ queryKey: petDiscountsQueryKey() })
      queryClient.invalidateQueries({ queryKey: invoicesQueryKey() })
    },
    // A rejected coupon means the pet's discounts changed since the page loaded; refetching
    // shows the price a retry would charge.
    onError: () => {
      queryClient.invalidateQueries({ queryKey: petDiscountsQueryKey(patientId) })
    },
  })
}
